import { Link } from "@inertiajs/react";

import { ActivationGuide } from "@/components/activation-guide";

interface DebuggerFaq {
  question: string;
  answer: string;
}

interface ComparisonRow {
  tool: string;
  checks: string;
  cache: string;
  image: string;
  misses: string;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  {
    tool: "Meta Sharing Debugger",
    checks: "Facebook share preview and Open Graph warnings. Facebook login required.",
    cache:
      "Running the URL in the Sharing Debugger refreshes scraped metadata. Meta publishes both a 24-hour update note and a 30-day automatic update. Image bytes are cached by URL.",
    image: "At least 1200×630 for high-res display. Minimum 200×200. File must not exceed 8 MB. Stay close to 1.91:1.",
    misses: "Does not preview X, LinkedIn, WhatsApp, Slack, or Messages. Does not draw a replacement image.",
  },
  {
    tool: "X card preview",
    checks: "No working official preview. The Card Validator preview was retired and not replaced in current X docs.",
    cache: "No public refresh tool. A current cache lifetime is not in first-party docs this page can cite.",
    image: "No current Cards size table on docs.x.com to cite. Set twitter:card and an absolute https image.",
    misses: "No official way to see the card or force a refetch. Falls back to og:* when twitter:* is absent.",
  },
  {
    tool: "LinkedIn Post Inspector",
    checks: "The preview LinkedIn will use, missing data, and when LinkedIn last fetched the page.",
    cache:
      "Inspect requests a re-scrape. LinkedIn Help says that refresh applies to new posts. Existing posts keep the old card.",
    image: "Sharing module: at least 1200×627, 1.91:1, at most 5 MB. Under 401 px wide shows as a thumbnail.",
    misses: "Does not preview other networks. Does not update posts that already used the URL.",
  },
  {
    tool: "WhatsApp",
    checks: "No debugger. Compose a message and wait up to 10 seconds for a preview above the composer.",
    cache: "The link-preview doc does not publish a refresh tool or a cache lifetime.",
    image:
      "Absolute URL, under 600 KB, at least 300 px wide, width/height of 4:1 or less. Tags in the first 300 KB of HTML.",
    misses: "No multi-network check and no documented cache clear. A file Meta accepts can still be too big here.",
  },
  {
    tool: "Messages (iMessage)",
    checks: "No public Open Graph debugger. TN3156 says Messages reads og:title and og:image for rich previews.",
    cache: "TN3156 does not document a refresh tool or a cache lifetime.",
    image: "At least 900 px wide. Under 150 px wide may be ignored or shown as an icon. Page resource limited to 1 MB.",
    misses: "No inspector. TN3156 says avoid text in the image because the preview size changes.",
  },
  {
    tool: "Slack",
    checks: "No public consumer debugger. Without an app unfurl, Slack crawls Open Graph and X card tags, plus oEmbed.",
    cache: "Slack's robots page says unfurl responses are cached globally for around 30 minutes.",
    image: "The unfurl docs cited here do not publish a single og:image pixel size.",
    misses: "Does not show Facebook, LinkedIn, or WhatsApp. Does not generate the image.",
  },
  {
    tool: "This open graph debugger",
    checks:
      "Public HTML, then og and twitter tags, then the image file. Previews Facebook, X, LinkedIn, Slack, Discord, and WhatsApp.",
    cache: "Shows the tags it fetched. It does not clear Meta, LinkedIn, X, WhatsApp, Messages, or Slack.",
    image:
      "Flags files that are not 1200×630, far from 1.91:1, over 1 MB, or not JPEG or PNG. The 1 MB line is ours, not Meta's or WhatsApp's.",
    misses:
      "It is not those crawlers. It can resolve a relative image URL, which hides a bug other crawlers still hit.",
  },
];

const API_SAMPLE = `POST https://ogpilot.com/api/v1/images
Content-Type: application/json

{"token":"YOUR_JWT_TOKEN"}

Required JWT claims: iss (verified domain), sub (API key prefix).
Optional: iat, template, title, description, path.
Sign the JWT on your server. Docs: https://ogpilot.com/docs`;

const JS_SAMPLE = `import { configure, createImage } from "og-pilot-js"

configure((config) => {
  config.apiKey = process.env.OG_PILOT_API_KEY
  config.domain = process.env.OG_PILOT_DOMAIN
})

const imageUrl = await createImage(
  {
    template: "blog_post",
    title: "Post title",
    description: "Post summary",
    path: "/pricing",
  },
  { iat: Math.floor(Date.now() / 1000) },
)

// Put imageUrl in og:image and twitter:image, then re-check the page.`;

const MCP_SAMPLE = `Remote MCP URL: https://ogpilot.com/mcp
Transport: Streamable HTTP
Auth: OAuth 2.1 with PKCE
Docs: https://ogpilot.com/docs/mcp

debug_open_graph
{ "url": "https://example.com/pricing" }

Returns score, score_label, meta_tags, image
(url, content_type, byte_size, width, height, aspect_ratio),
issues, and suggested_meta. Read-only.

generate_og_image
{ "url": "https://example.com/pricing" }
Optional style. Default is auto-detect.

Debug works on a guest connector session.
generate_og_image needs a signed-in account with an active plan.`;

const linkClass = "font-medium text-orange-700 hover:underline dark:text-orange-300";

const sectionClass = "mx-auto mt-16 max-w-4xl scroll-mt-20 md:mt-20";

function CodeSample({ label, code }: { label: string; code: string }) {
  return (
    <div className="mt-6">
      <p className="text-sm font-semibold text-gray-900 dark:text-white">{label}</p>
      <pre className="mt-2 overflow-x-auto rounded-md bg-gray-50 p-4 text-xs leading-5 dark:bg-gray-900/60">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export function OpenGraphDebuggerGuide({ faqs = [] }: { faqs?: DebuggerFaq[] }) {
  return (
    <div id="open-graph-debugger-guide">
      <section className={sectionClass}>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl dark:text-white">
          What each open graph debugger actually checks
        </h2>
        <p className="text-muted-foreground mt-4 text-base leading-7">
          An <strong>open graph debugger</strong> answers one question: what card will this URL get? An{" "}
          <strong>og image checker</strong> should also say whether the file is reachable, large enough, and the right
          type. Meta, LinkedIn, X, WhatsApp, Messages, and Slack do not share one tool, and they do not share one image
          rule. The tag names themselves come from the{" "}
          <a href="https://ogp.me/" className={linkClass} target="_blank" rel="noopener noreferrer">
            Open Graph protocol
          </a>
          . That site defines properties. It is not a multi-network preview.
        </p>
        <p className="text-muted-foreground mt-3 text-base leading-7">
          The table uses the vendor pages linked under it. Where a current doc does not state a size or a cache
          lifetime, the cell says so. This page does not fill those gaps with a round number.
        </p>
        <div className="mt-6 overflow-x-auto rounded-lg border bg-white shadow-sm dark:bg-gray-800">
          <table className="w-full min-w-[56rem] border-collapse text-left text-sm text-gray-700 dark:text-gray-300">
            <thead>
              <tr className="border-b bg-gray-50 dark:bg-gray-900/40">
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">Tool</th>
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">What it checks</th>
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">Cache refresh</th>
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">Image size</th>
                <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">What it misses</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row) => (
                <tr key={row.tool} className="border-b align-top last:border-b-0">
                  <th className="px-3 py-3 font-semibold text-gray-900 dark:text-white">{row.tool}</th>
                  <td className="px-3 py-3">{row.checks}</td>
                  <td className="px-3 py-3">{row.cache}</td>
                  <td className="px-3 py-3">{row.image}</td>
                  <td className="px-3 py-3">{row.misses}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-muted-foreground mt-4 text-sm leading-6">
          Sources:{" "}
          <a
            href="https://developers.facebook.com/tools/debug/"
            className={linkClass}
            target="_blank"
            rel="noopener noreferrer"
          >
            Meta Sharing Debugger
          </a>
          ,{" "}
          <a
            href="https://developers.facebook.com/docs/sharing/webmasters/images/"
            className={linkClass}
            target="_blank"
            rel="noopener noreferrer"
          >
            Images in Link Shares
          </a>{" "}
          (updated June 30, 2026: 1200×630, 600×315, 200×200, 8 MB, 1.91:1),{" "}
          <a
            href="https://developers.facebook.com/docs/sharing/best-practices"
            className={linkClass}
            target="_blank"
            rel="noopener noreferrer"
          >
            Sharing best practices
          </a>{" "}
          (February 23, 2022: debugger refresh versus a standard 24-hour update),{" "}
          <a
            href="https://developers.facebook.com/docs/sharing/webmasters/getting-started/change-link/"
            className={linkClass}
            target="_blank"
            rel="noopener noreferrer"
          >
            change an existing link
          </a>{" "}
          (July 20, 2018: automatic updates can take 30 days; Graph API{" "}
          <code>POST /?id=&#123;url&#125;&amp;scrape=true</code>; use a new image URL),{" "}
          <a
            href="https://www.linkedin.com/post-inspector/"
            className={linkClass}
            target="_blank"
            rel="noopener noreferrer"
          >
            LinkedIn Post Inspector
          </a>
          ,{" "}
          <a
            href="https://www.linkedin.com/help/linkedin/answer/a521928"
            className={linkClass}
            target="_blank"
            rel="noopener noreferrer"
          >
            make a site shareable
          </a>
          ,{" "}
          <a
            href="https://www.linkedin.com/help/linkedin/answer/a6233775"
            className={linkClass}
            target="_blank"
            rel="noopener noreferrer"
          >
            refresh a URL
          </a>
          ,{" "}
          <a
            href="https://developers.facebook.com/docs/whatsapp/link-previews/"
            className={linkClass}
            target="_blank"
            rel="noopener noreferrer"
          >
            WhatsApp link previews
          </a>{" "}
          (updated May 21, 2026),{" "}
          <a
            href="https://developer.apple.com/documentation/technotes/tn3156-create-rich-previews-for-messages"
            className={linkClass}
            target="_blank"
            rel="noopener noreferrer"
          >
            Apple TN3156
          </a>
          ,{" "}
          <a href="https://api.slack.com/robots" className={linkClass} target="_blank" rel="noopener noreferrer">
            Slack robots
          </a>
          , and{" "}
          <a
            href="https://docs.slack.dev/messaging/unfurling-links-in-messages/"
            className={linkClass}
            target="_blank"
            rel="noopener noreferrer"
          >
            Slack unfurls
          </a>
          . Meta&apos;s 24-hour note and 30-day note are both published. Scrape again, and change the image URL, instead
          of picking one interval.
        </p>
      </section>

      <section className={sectionClass}>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl dark:text-white">
          What an og image checker still misses
        </h2>
        <div className="text-muted-foreground mt-4 space-y-4 text-base leading-7">
          <p>
            Platform tools tell you what <em>they</em> stored. They do not tell you that the next network will store
            something else. These are the failures that survive a green debugger.
          </p>
          <p>
            <strong className="text-gray-900 dark:text-white">Stale caches.</strong> Meta caches the image by its URL
            and tells you to publish the new file at a new URL. LinkedIn&apos;s refresh does not rewrite posts that
            already went out. Slack&apos;s robots page puts the unfurl cache at around 30 minutes. X, WhatsApp, and
            Messages do not publish a button for this. Run the URL here to see the tags that are live now, then use the
            network&apos;s own tool where one exists. This checker does not call those tools.
          </p>
          <p>
            <strong className="text-gray-900 dark:text-white">Redirects.</strong> This checker follows up to five
            redirects and rejects a private hop. Share the final public URL anyway. <code>og:url</code> should be that
            page, not a tracking link. WhatsApp&apos;s doc asks for a canonical URL without session parameters.
          </p>
          <p>
            <strong className="text-gray-900 dark:text-white">Relative og:image URLs.</strong> WhatsApp requires an
            absolute image URL. This checker joins a relative URL to the page before it fetches the file, so a preview
            can look fine here and fail for a crawler that does not do that join. Publish <code>https://…</code>.
          </p>
          <p>
            <strong className="text-gray-900 dark:text-white">Wrong content type.</strong> A login wall or a soft 404
            often returns HTML where the image should be. The checker reports the response content type and flags
            anything that is not JPEG or PNG. A platform debugger often just shows a missing image. JPEG and PNG are
            this checker&apos;s preferred formats. Meta&apos;s image doc does not limit you to those two, and
            LinkedIn&apos;s ad specs (a different surface) allow JPG, PNG, or GIF.
          </p>
          <p>
            <strong className="text-gray-900 dark:text-white">Oversized images.</strong> Meta allows up to 8 MB.
            LinkedIn&apos;s sharing module allows 5 MB. WhatsApp wants the image under 600 KB. This checker flags files
            over 1 MB because that is its own recommendation, which is stricter than Meta and looser than WhatsApp. A
            file that passes Meta can still fail WhatsApp. The MCP tool <code>compress_image</code> resizes a public
            image URL. Its default dimensions are 1200×630.
          </p>
          <p>
            <strong className="text-gray-900 dark:text-white">Missing twitter:card.</strong> X no longer offers the Card
            Validator preview, so a Facebook-only debugger will not mention the gap. This checker subtracts from the
            score when <code>twitter:card</code> is missing and still draws an X preview from the tags it found. The
            suggested meta block defaults that card to <code>summary_large_image</code>.
          </p>
          <p>
            <strong className="text-gray-900 dark:text-white">One image for every URL.</strong> A homepage screenshot
            does not describe a pricing page or a post. Put the tags in the first HTML response. WhatsApp only reads
            markup inside the first 300 KB, and a crawler that does not run the app will not see tags inserted after
            load. When each URL needs its own card, generate it. The{" "}
            <Link href="/og-image-api" className={linkClass}>
              OG image API
            </Link>{" "}
            returns a CDN URL for <code>og:image</code>. The{" "}
            <Link href="/nextjs-og-image-generator" className={linkClass}>
              Next.js guide
            </Link>{" "}
            shows that URL from <code>generateMetadata</code>. The{" "}
            <Link href="/wordpress" className={linkClass}>
              WordPress plugin
            </Link>{" "}
            writes the tags for posts, pages, and WooCommerce products.
          </p>
        </div>
      </section>

      <section id="copy-paste" className={sectionClass}>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl dark:text-white">
          Fix the card with the API or MCP
        </h2>
        <p className="text-muted-foreground mt-4 text-base leading-7">
          The checker above tells you the image is weak. These calls replace it. Create an API key, sign requests on
          your server, and put the returned URL in <code>og:image</code> and <code>twitter:image</code>. Then run the
          same public URL through the checker again. Domain verification for the API is a DNS TXT record, documented at{" "}
          <Link href="/docs" className={linkClass}>
            /docs
          </Link>
          .
        </p>
        <CodeSample label="API request" code={API_SAMPLE} />
        <CodeSample label="JavaScript SDK" code={JS_SAMPLE} />
        <p className="text-muted-foreground mt-4 text-sm leading-6">
          <code>POST https://ogpilot.com/api/v1/images</code> takes a JSON body field <code>token</code>. Required JWT
          claims are <code>iss</code> and <code>sub</code>. Pass <code>iat</code> when you want the documented 24-hour
          cache window. The SDK call matches the{" "}
          <Link href="/og-image-api" className={linkClass}>
            OG image API guide
          </Link>
          . Prefer an official SDK over hand-rolled signing.
        </p>
        <CodeSample label="MCP: debug, then generate" code={MCP_SAMPLE} />
        <p className="text-muted-foreground mt-4 text-sm leading-6">
          <code>debug_open_graph</code> is the read-only inspect tool. <code>generate_og_image</code> builds the
          replacement. Both are on the remote server at <code>https://ogpilot.com/mcp</code>. Connector steps for Claude
          and ChatGPT developer mode are on{" "}
          <Link href="/docs/mcp" className={linkClass}>
            /docs/mcp
          </Link>
          . A trial is not enough for generation. The tool returns <code>plan_required</code> and does not start a
          checkout.
        </p>
      </section>

      <ActivationGuide
        title="Create an API key, or install MCP"
        links={[
          {
            href: "/sign_up",
            label: "Create an API key",
            detail: "Sign image URLs from your server after the domain is verified.",
          },
          {
            href: "/docs/mcp",
            label: "Install MCP",
            detail: "Add https://ogpilot.com/mcp and call debug_open_graph, then generate_og_image.",
          },
          {
            href: "/new",
            label: "Generate one image",
            detail: "Build a 1200×630 card for the URL you just checked, before you wire auth.",
          },
          {
            href: "/og-image-api",
            label: "OG image API",
            detail: "Hosted API next to @vercel/og and a DIY Satori pipeline.",
          },
          {
            href: "/nextjs-og-image-generator",
            label: "Next.js OG images",
            detail: "Metadata file, ImageResponse, or this API from generateMetadata.",
          },
          {
            href: "/wordpress",
            label: "WordPress plugin",
            detail: "Generated og:image for posts, pages, and WooCommerce, without a theme secret.",
          },
        ]}
      >
        <p>
          Check the live URL on this page first. If the image is the weak part, generate a replacement and publish that
          URL. Read{" "}
          <Link href="/docs" className={linkClass}>
            the API docs
          </Link>{" "}
          for JWT claims, and{" "}
          <Link href="/docs/mcp" className={linkClass}>
            the MCP guide
          </Link>{" "}
          for the connector. Carrd still uploads a file. The{" "}
          <Link href="/carrd-social-image" className={linkClass}>
            Carrd social image guide
          </Link>{" "}
          is that path.
        </p>
      </ActivationGuide>

      {faqs.length > 0 ? (
        <section className={sectionClass}>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 md:text-3xl dark:text-white">
            Open graph debugger questions
          </h2>
          <div className="mt-6 space-y-6">
            {faqs.map((faq) => (
              <article key={faq.question}>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{faq.question}</h3>
                <p className="text-muted-foreground mt-2 text-sm leading-6 md:text-base">{faq.answer}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
